import React from 'react';
import type { ReactNode } from 'react';
import { Box, Flex } from '@withjoy/joykit';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { galleryDesktopAspectX, galleryDesktopAspectY, galleryMobileAspectX, galleryMobileAspectY } from '../../../CardsConfig';

const VALUE_PROP_STYLES = {
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  width: '100%',
  height: '100%',
  border: '1px solid hsl(0, 0%, 90%)',
} as const;

type ValuePropTileShellProps = Readonly<{
  row: number;
  column: number;
  columns?: number;
  children: ReactNode;
}>;

/**
 * Shared chrome for value-prop gallery tiles — the single-prop {@link ValuePropCard} and the
 * carousel {@link ValuePropListCard}. Owns grid placement, the responsive aspect ratio, and the
 * framed surface that wraps the tile's ContentCarousel, so the two tiles can't drift apart.
 */
export const ValuePropTileShell = ({ row, column, columns = 1, children }: ValuePropTileShellProps) => {
  const isMobile = useIsMobileScreen();

  // Locate the tile within the grid; -1 signals "flow" (used during search).
  const gridAreaValue = row === -1 || column === -1 ? 'auto' : `${row + 1} / ${column + 1} / ${row + 1} / span ${columns}`;
  // Aspect ratio for mobile / desktop factoring in width in columns. Matches the gallery tile
  // ratios so all tiles in a row share the same height.
  const aspectRatioX = isMobile ? galleryMobileAspectX : galleryDesktopAspectX;
  const aspectRatioY = isMobile ? galleryMobileAspectY : galleryDesktopAspectY;
  const aspectRatio = `${Number(aspectRatioX) * columns} / ${aspectRatioY}`;

  return (
    <Flex
      gridArea={gridAreaValue}
      aspectRatio={aspectRatio}
      background="hsla(45, 12%, 93%, 1)"
      width="100%"
      height="100%"
    >
      <Box
        height="100%"
        width="100%"
        overflow="hidden"
        position="relative"
        style={VALUE_PROP_STYLES}
      >
        {children}
      </Box>
    </Flex>
  );
};
